import { useState, useMemo, useRef, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, CalendarIcon, CalendarDays, PlayCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { formatMeetingPattern } from "@/lib/dateUtils";
import { VideoPlayer } from "./VideoPlayer";

interface ConditionalOn {
  field: string;
  value?: string;
  value_in?: string[];
}

interface FormField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'radio' | 'date';
  required: boolean;
  placeholder?: string;
  options?: string[];
  conditional_on?: ConditionalOn;
}

interface JourneyFormDataStepProps {
  stepId: string;
  userId: string;
  fields: FormField[];
  autoCreateResolution: boolean;
  isCompleted: boolean;
  existingData?: Record<string, any>;
  onComplete: () => void;
  introText?: string;
  videoUrl?: string;
  videoRequiredWatchPercent?: number;
  initialVideoProgress?: number;
}

export function JourneyFormDataStep({
  stepId,
  userId,
  fields,
  autoCreateResolution,
  isCompleted,
  existingData,
  onComplete,
  introText,
  videoUrl,
  videoRequiredWatchPercent = 95,
  initialVideoProgress = 0,
}: JourneyFormDataStepProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, string>>(existingData || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  // Video tracking state
  const [videoWatchedPercent, setVideoWatchedPercent] = useState(initialVideoProgress);
  const [videoCompleted, setVideoCompleted] = useState(!videoUrl || initialVideoProgress >= videoRequiredWatchPercent);
  const videoProgressRef = useRef(initialVideoProgress);
  const lastSavedPercentRef = useRef(Math.floor(initialVideoProgress / 5));

  // Update ref when progress changes
  useEffect(() => {
    videoProgressRef.current = videoWatchedPercent;
  }, [videoWatchedPercent]);

  // Save video progress on unmount
  useEffect(() => {
    return () => {
      const currentProgress = videoProgressRef.current;
      if (currentProgress > 0 && currentProgress > initialVideoProgress) {
        supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: stepId,
            status: 'active',
            action_data: { video_watch_percent: currentProgress },
          }, { onConflict: 'user_id,step_id' })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
          });
      }
    };
  }, [userId, stepId, initialVideoProgress, queryClient]);

  const handleVideoProgress = async (percent: number) => {
    setVideoWatchedPercent(percent);
    
    const currentInterval = Math.floor(percent / 5);
    if (currentInterval > lastSavedPercentRef.current) {
      lastSavedPercentRef.current = currentInterval;
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { video_watch_percent: percent },
        }, { onConflict: 'user_id,step_id' });
      
      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
    }
  };

  const handleVideoComplete = () => {
    setVideoCompleted(true);
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Check if a field should be visible based on conditional_on
  const isFieldVisible = (field: FormField): boolean => {
    if (!field.conditional_on) return true;
    const dependentValue = formData[field.conditional_on.field];
    
    // Support value_in for matching any of multiple values
    if (field.conditional_on.value_in) {
      return field.conditional_on.value_in.includes(dependentValue);
    }
    
    return dependentValue === field.conditional_on.value;
  };

  // Calculate meeting pattern preview for governance meetings
  const governanceMeetingPreview = useMemo(() => {
    const frequency = formData.una_meeting_frequency;
    if (!frequency) return null;
    
    // For weekly/bi-weekly, use the day selector
    if (frequency === 'Weekly' || frequency === 'Bi-Weekly') {
      const day = formData.una_meeting_day;
      if (!day) return null;
      return formatMeetingPattern(frequency, undefined, day);
    }
    
    // For monthly, use occurrence + day
    if (frequency === 'Monthly') {
      const occurrence = formData.una_meeting_occurrence;
      const day = formData.una_meeting_day_monthly;
      if (!occurrence || !day) return null;
      return formatMeetingPattern(frequency, occurrence, day);
    }
    
    return null;
  }, [formData.una_meeting_frequency, formData.una_meeting_day, formData.una_meeting_occurrence, formData.una_meeting_day_monthly]);

  // Calculate meeting pattern preview for observance meetings
  const observanceMeetingPreview = useMemo(() => {
    if (formData.separate_observance_meetings !== 'Yes') return null;
    
    const frequency = formData.observance_only_frequency;
    if (!frequency) return null;
    
    // For weekly/bi-weekly, use the day selector
    if (frequency === 'Weekly' || frequency === 'Bi-Weekly') {
      const day = formData.observance_meeting_day;
      if (!day) return null;
      return formatMeetingPattern(frequency, undefined, day);
    }
    
    // For monthly, use occurrence + day
    if (frequency === 'Monthly') {
      const occurrence = formData.observance_meeting_occurrence;
      const day = formData.observance_meeting_day_monthly;
      if (!occurrence || !day) return null;
      return formatMeetingPattern(frequency, occurrence, day);
    }
    
    return null;
  }, [formData.separate_observance_meetings, formData.observance_only_frequency, formData.observance_meeting_day, formData.observance_meeting_occurrence, formData.observance_meeting_day_monthly]);

  const isFormValid = () => {
    return fields
      .filter(f => f.required && isFieldVisible(f))
      .every(f => formData[f.name]?.trim());
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsSubmitting(true);
    try {
      // Build structured form data for the edge function
      const frequency = formData.una_meeting_frequency || 'Monthly';
      
      // Build the meeting pattern based on frequency
      let governancePattern: Record<string, any> | null = null;
      let calculatedFirstDate: string | null = null;
      
      if (frequency === 'Weekly' || frequency === 'Bi-Weekly') {
        const day = formData.una_meeting_day;
        if (day) {
          governancePattern = { day };
          const preview = formatMeetingPattern(frequency, undefined, day);
          calculatedFirstDate = preview.nextDate?.toISOString() || null;
        }
      } else if (frequency === 'Monthly') {
        const occurrence = formData.una_meeting_occurrence;
        const day = formData.una_meeting_day_monthly;
        if (occurrence && day) {
          governancePattern = { occurrence, day };
          const preview = formatMeetingPattern(frequency, occurrence, day);
          calculatedFirstDate = preview.nextDate?.toISOString() || null;
        }
      } else {
        // Quarterly/Annually - use the date picker
        calculatedFirstDate = formData.first_governance_meeting_date || null;
      }
      
      const structuredFormData: Record<string, any> = {
        // Governance meeting cadence (always created)
        una_meeting_cadence: {
          frequency,
          pattern: governancePattern,
          start_date: calculatedFirstDate,
        },
        una_meeting_frequency: frequency,
        first_governance_meeting_date: calculatedFirstDate || '',
        
        // Separate observance meetings (conditional)
        separate_observance_meetings: formData.separate_observance_meetings === 'Yes',
      };

      // Only include observance cadence if they opted for separate meetings
      if (formData.separate_observance_meetings === 'Yes') {
        const obsFrequency = formData.observance_only_frequency;
        let observancePattern: Record<string, any> | null = null;
        let obsCalculatedDate: string | null = null;
        
        if (obsFrequency === 'Weekly' || obsFrequency === 'Bi-Weekly') {
          const day = formData.observance_meeting_day;
          if (day) {
            observancePattern = { day };
            const preview = formatMeetingPattern(obsFrequency, undefined, day);
            obsCalculatedDate = preview.nextDate?.toISOString() || null;
          }
        } else if (obsFrequency === 'Monthly') {
          const occurrence = formData.observance_meeting_occurrence;
          const day = formData.observance_meeting_day_monthly;
          if (occurrence && day) {
            observancePattern = { occurrence, day };
            const preview = formatMeetingPattern(obsFrequency, occurrence, day);
            obsCalculatedDate = preview.nextDate?.toISOString() || null;
          }
        } else {
          obsCalculatedDate = formData.first_observance_meeting_date || null;
        }
        
        structuredFormData.observance_cadence = {
          frequency: obsFrequency || null,
          pattern: observancePattern,
          start_date: obsCalculatedDate,
        };
        structuredFormData.observance_only_frequency = obsFrequency || '';
        structuredFormData.first_observance_meeting_date = obsCalculatedDate || '';
      }

      // Call edge function
      const { data, error } = await supabase.functions.invoke('complete-journey-step-with-resolution', {
        body: {
          userId,
          stepId,
          formData: structuredFormData,
        },
      });

      if (error) throw error;

      const resolutionCount = data?.resolutionsCreated?.length || 0;
      const taskCount = data?.tasksCreated?.length || 0;
      
      let description = "Your information has been saved successfully.";
      if (resolutionCount > 0 && taskCount > 0) {
        description = `Your information has been saved. ${resolutionCount} resolution(s) and ${taskCount} recurring task(s) have been created.`;
      } else if (resolutionCount > 0) {
        description = `Your information has been saved and ${resolutionCount} resolution(s) have been created.`;
      } else if (taskCount > 0) {
        description = `Your information has been saved and ${taskCount} recurring task(s) have been created.`;
      }

      toast({
        title: "Information saved",
        description,
      });

      onComplete();
    } catch (error) {
      console.error('Error saving form data:', error);
      toast({
        title: "Error",
        description: "Failed to save information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted) {
    // Show submitted data in review mode
    const submittedData = existingData || {};
    const hasData = Object.keys(submittedData).length > 0;
    
    return (
      <div className="space-y-6">
        {/* Show video if one exists - allow replay after completion */}
        {videoUrl && (
          <div className="space-y-3">
            <div className="relative">
              <VideoPlayer
                videoUrl={videoUrl}
                requiredWatchPercent={0}
                onProgress={() => {}}
                onComplete={() => {}}
                initialProgress={0}
              />
              <div className="absolute top-3 right-3 bg-green-600/90 text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg pointer-events-none">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed</span>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <PlayCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-green-800">Video Completed</p>
                <p className="text-sm text-green-700">You can rewatch this video at any time.</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-600 mb-3">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Information submitted</span>
          </div>
          
          {hasData ? (
            <div className="bg-muted/30 rounded-lg p-4 space-y-3">
              {fields.map((field) => {
                const value = submittedData[field.name];
                if (!value) return null;
                
                // Format date values
                let displayValue = value;
                if (field.type === 'date' && value) {
                  try {
                    displayValue = format(new Date(value), 'MMMM d, yyyy');
                  } catch {
                    displayValue = value;
                  }
                }
                
                return (
                  <div key={field.name} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-sm font-medium text-muted-foreground">{field.label}:</span>
                    <span className="text-sm">{displayValue}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Form data saved.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Video Section - if video exists */}
      {videoUrl && (
        <div className="space-y-3">
          <Card>
            <CardContent className="pt-6">
              <VideoPlayer
                videoUrl={videoUrl}
                requiredWatchPercent={videoRequiredWatchPercent}
                onProgress={handleVideoProgress}
                onComplete={handleVideoComplete}
                initialProgress={initialVideoProgress}
              />
            </CardContent>
          </Card>
          
          {!videoCompleted && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <PlayCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Watch the Video First</p>
                <p className="text-sm text-blue-700">Please watch the video above before completing the form.</p>
              </div>
            </div>
          )}
          
          {videoCompleted && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">Video Completed</p>
                <p className="text-sm text-green-700">You may now complete the form below.</p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Intro Text */}
      {introText && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">{introText}</p>
        </div>
      )}
      
      {/* Form Fields */}
      <div className="space-y-4">
        {fields.map((field) => {
          // Skip rendering if conditional field is not met
          if (!isFieldVisible(field)) return null;

          return (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>
                {field.label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>

              {field.type === 'text' && (
                <Input
                  id={field.name}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                />
              )}

              {field.type === 'textarea' && (
                <Textarea
                  id={field.name}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                />
              )}

              {field.type === 'select' && field.options && (
                <Select
                  value={formData[field.name] || ''}
                  onValueChange={(value) => handleFieldChange(field.name, value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={field.placeholder || 'Select an option'} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {field.type === 'radio' && field.options && (
                <RadioGroup
                  value={formData[field.name] || ''}
                  onValueChange={(value) => handleFieldChange(field.name, value)}
                  className="flex flex-row gap-6"
                >
                  {field.options.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem value={option} id={`${field.name}-${option}`} />
                      <Label htmlFor={`${field.name}-${option}`} className="font-normal cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {field.type === 'date' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData[field.name] && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData[field.name] ? (
                        format(new Date(formData[field.name]), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData[field.name] ? new Date(formData[field.name]) : undefined}
                      onSelect={(date) => handleFieldChange(field.name, date ? date.toISOString() : '')}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              )}
              {/* Meeting Pattern Preview - show after pattern selectors */}
              {field.name === 'una_meeting_day' && governanceMeetingPreview && (
                <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
                  <CalendarDays className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {governanceMeetingPreview.description}
                    </p>
                    {governanceMeetingPreview.nextDate && (
                      <p className="text-xs text-muted-foreground">
                        Next meeting: {format(governanceMeetingPreview.nextDate, 'MMMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {field.name === 'una_meeting_day_monthly' && governanceMeetingPreview && (
                <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
                  <CalendarDays className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {governanceMeetingPreview.description}
                    </p>
                    {governanceMeetingPreview.nextDate && (
                      <p className="text-xs text-muted-foreground">
                        Next meeting: {format(governanceMeetingPreview.nextDate, 'MMMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {field.name === 'observance_meeting_day' && observanceMeetingPreview && (
                <div className="mt-3 p-3 bg-secondary/20 border border-secondary/40 rounded-lg flex items-center gap-3">
                  <CalendarDays className="h-5 w-5 text-secondary-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {observanceMeetingPreview.description}
                    </p>
                    {observanceMeetingPreview.nextDate && (
                      <p className="text-xs text-muted-foreground">
                        Next observance: {format(observanceMeetingPreview.nextDate, 'MMMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {field.name === 'observance_meeting_day_monthly' && observanceMeetingPreview && (
                <div className="mt-3 p-3 bg-secondary/20 border border-secondary/40 rounded-lg flex items-center gap-3">
                  <CalendarDays className="h-5 w-5 text-secondary-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {observanceMeetingPreview.description}
                    </p>
                    {observanceMeetingPreview.nextDate && (
                      <p className="text-xs text-muted-foreground">
                        Next observance: {format(observanceMeetingPreview.nextDate, 'MMMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Auto Resolution Notice */}
      {autoCreateResolution && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Submitting this form will automatically create resolutions 
            for your meeting schedule.
          </p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting || (videoUrl && !videoCompleted)}
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : videoUrl && !videoCompleted ? (
            "Watch Video to Continue"
          ) : (
            "Acknowledge and Continue"
          )}
        </Button>
      </div>
    </div>
  );
}
